import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { useUpdateCryptoOnchainPlatformSettings } from '@/lib/api/crypto-onchain-platform';

import { CopyableValue } from './CopyableValue';
import { PayoutAddressFields } from './PayoutAddressFields';
import {
  diffPayoutAddresses,
  isPayoutAddressDraftValid,
  toPayoutAddressDraft,
  type PayoutAddressDraft,
} from './payout-address';
import { PAYOUT_ADDRESS_KINDS, type PayoutAddressKind, type PlatformSettings } from './types';

type Props = {
  settings: PlatformSettings | null;
};

/**
 * The six platform wallets, read-only with copy, and the superadmin-only edit
 * dialog. Changing a wallet redirects every merchant's platform share on that
 * chain from the next sweep on, which is why the gate mirrors
 * `superadminProcedure` on `updateSettings` and the form sits behind a dialog.
 */
export function PlatformWalletsPanel({ settings }: Props) {
  const { t } = useTranslation('manageCryptoPayments');
  const { isSuperAdmin } = useSessionClaims();
  const updateSettings = useUpdateCryptoOnchainPlatformSettings();

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<PayoutAddressDraft>(() => toPayoutAddressDraft(settings?.wallets));

  const changes = diffPayoutAddresses(settings?.wallets, draft);
  const hasChanges = Object.keys(changes).length > 0;
  const isDraftValid = isPayoutAddressDraftValid(draft);
  const canSave = hasChanges && isDraftValid && !updateSettings.isPending;
  const isEditDisabled = !settings?.configured;

  const handleOpenChange = (isOpen: boolean) => {
    setIsEditing(isOpen);
    // The dialog always opens on what the server currently holds, never on a
    // draft abandoned earlier.
    if (isOpen) setDraft(toPayoutAddressDraft(settings?.wallets));
  };

  const handleFieldChange = (kind: PayoutAddressKind, value: string) => {
    setDraft((current) => ({ ...current, [kind]: value }));
  };

  const handleSave = () => {
    if (!canSave) return;
    updateSettings.mutate({ wallets: changes }, { onSuccess: () => setIsEditing(false) });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>{t('wallets.title')}</CardTitle>
            <CardDescription>{t('wallets.description')}</CardDescription>
          </div>

          {isSuperAdmin ? (
            <Dialog open={isEditing} onOpenChange={handleOpenChange}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={isEditDisabled}>
                  <Pencil className="h-3.5 w-3.5" />
                  {t('wallets.edit')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('wallets.editTitle')}</DialogTitle>
                  <DialogDescription>{t('wallets.editDescription')}</DialogDescription>
                </DialogHeader>

                <PayoutAddressFields
                  values={draft}
                  onChange={handleFieldChange}
                  idPrefix="platform-wallet"
                  isDisabled={updateSettings.isPending}
                />

                <DialogFooter className="items-center">
                  {!hasChanges && (
                    <span className="text-muted-foreground mr-auto text-xs">{t('wallets.noChanges')}</span>
                  )}
                  <Button variant="ghost" onClick={() => setIsEditing(false)}>
                    {t('actions.cancel')}
                  </Button>
                  <Button disabled={!canSave} onClick={handleSave}>
                    {updateSettings.isPending ? t('actions.saving') : t('wallets.save')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : (
            <p className="text-muted-foreground text-xs">{t('wallets.superadminOnly')}</p>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PAYOUT_ADDRESS_KINDS.map((kind) => {
            const wallet = settings?.wallets[kind] ?? null;
            const chainLabel = t(`chains.${kind}`);
            return (
              <div key={kind} className="space-y-1">
                <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                  {chainLabel}
                </dt>
                <dd className="text-sm">
                  {wallet ? (
                    <CopyableValue value={wallet} label={chainLabel} />
                  ) : (
                    <span className="text-muted-foreground">{t('wallets.notSet')}</span>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </CardContent>
    </Card>
  );
}
